/**
 * The per-country shape of an EU VAT number, as Stripe's `eu_vat` tax id takes it.
 *
 * WHY THIS EXISTS. `^[A-Z]{2}[A-Za-z0-9]{2,12}$` — the shape rule this replaces —
 * accepts `EE1232412313`, which Stripe answers with `Invalid value for eu_vat`.
 * That refusal arrives as a generic failure with nothing to point at, so the
 * owner is told billing is unavailable rather than that their VAT number has one
 * digit too many. The country is the only thing that decides the shape, and the
 * prefix is the only country signal this form carries.
 *
 * SOURCE, AND WHY IT IS A MIRROR. Every entry matches `VAT_FORMATS` in the Edge
 * Function's `billing-rules.ts` pattern for pattern — that is the boundary, and
 * a client rule that disagreed in either direction would be a bug: stricter, and
 * the form refuses what the server would have saved; looser, and the refusal
 * comes back as a round trip the client could have answered itself. Both tables
 * are built from the `eu_vat` rows of docs.stripe.com/billing/customer/tax-ids
 * (read 2026-09-07), widened only where a registration has documented length
 * variants (BG, CZ, LT, RO, XI). The test pins that every documented example is
 * accepted: this form is the ONLY way out of `details_missing`, so refusing a
 * number Stripe would have taken costs an organisation every building it wanted
 * to add.
 *
 * `country` is the ISO 3166-1 alpha-2 code the Stripe Customer address takes,
 * which is not always the registration prefix: Greece registers as EL and
 * Northern Ireland as XI. It matches `VAT_PREFIX_COUNTRY` in the Edge Function's
 * `billing-rules.ts` exactly — the server derives the address from it.
 */
interface VatFormat {
  /** ISO 3166-1 alpha-2, for the address country and the country name. */
  country: string;
  /** Anchored against the part AFTER the two-character prefix. */
  block: RegExp;
  /** Stripe's documented example, shown verbatim in the error message. */
  example: string;
}

export const VAT_FORMATS: ReadonlyMap<string, VatFormat> = new Map([
  ["AT", { country: "AT", block: /^U\d{8}$/, example: "ATU12345678" }],
  ["BE", { country: "BE", block: /^\d{10}$/, example: "BE0123456789" }],
  ["BG", { country: "BG", block: /^\d{9,10}$/, example: "BG0123456789" }],
  ["CY", { country: "CY", block: /^\d{8}[A-Z]$/, example: "CY12345678Z" }],
  ["CZ", { country: "CZ", block: /^\d{8,10}$/, example: "CZ1234567890" }],
  ["DE", { country: "DE", block: /^\d{9}$/, example: "DE123456789" }],
  ["DK", { country: "DK", block: /^\d{8}$/, example: "DK12345678" }],
  ["EE", { country: "EE", block: /^\d{9}$/, example: "EE123456789" }],
  ["EL", { country: "GR", block: /^\d{9}$/, example: "EL123456789" }],
  ["ES", { country: "ES", block: /^[A-Z0-9]\d{7}[A-Z0-9]$/, example: "ESA1234567Z" }],
  ["FI", { country: "FI", block: /^\d{8}$/, example: "FI12345678" }],
  ["FR", { country: "FR", block: /^[A-Z0-9]{2}\d{9}$/, example: "FRAB123456789" }],
  ["HR", { country: "HR", block: /^\d{11}$/, example: "HR12345678912" }],
  ["HU", { country: "HU", block: /^\d{8}$/, example: "HU12345678" }],
  ["IE", { country: "IE", block: /^\d{7}[A-Z]{1,2}$/, example: "IE1234567AB" }],
  ["IT", { country: "IT", block: /^\d{11}$/, example: "IT12345678912" }],
  ["LT", { country: "LT", block: /^\d{9}$|^\d{12}$/, example: "LT123456789123" }],
  ["LU", { country: "LU", block: /^\d{8}$/, example: "LU12345678" }],
  ["LV", { country: "LV", block: /^\d{11}$/, example: "LV12345678912" }],
  ["MT", { country: "MT", block: /^\d{8}$/, example: "MT12345678" }],
  ["NL", { country: "NL", block: /^\d{9}B\d{2}$/, example: "NL123456789B12" }],
  ["PL", { country: "PL", block: /^\d{10}$/, example: "PL1234567890" }],
  ["PT", { country: "PT", block: /^\d{9}$/, example: "PT123456789" }],
  ["RO", { country: "RO", block: /^\d{2,10}$/, example: "RO1234567891" }],
  ["SE", { country: "SE", block: /^\d{12}$/, example: "SE123456789123" }],
  ["SI", { country: "SI", block: /^\d{8}$/, example: "SI12345678" }],
  ["SK", { country: "SK", block: /^\d{10}$/, example: "SK1234567891" }],
  ["XI", { country: "GB", block: /^\d{9}$|^\d{12}$/, example: "XI123456789" }],
]);

/**
 * The typed value as Stripe reads it: uppercase, with the spaces, dots and
 * hyphens people copy off an invoice removed.
 *
 * Stripe stores and validates the value verbatim, so `EE 123 456 789` is a
 * refusal rather than a formatting preference — normalising here is what makes
 * the pasted form of a correct number correct.
 */
export function normaliseVatNumber(value: string): string {
  return value.replace(/[\s.\-/]/g, "").toUpperCase();
}

/** What is wrong with a VAT number, or null when nothing is. */
export type VatIssue = { kind: "prefix" } | { kind: "format"; country: string; example: string };

/**
 * Check a VAT number against its own country's format.
 *
 * An empty value is accepted: a company with no VAT number is invoiced without
 * the reverse charge, which is a legitimate answer this form must keep taking.
 */
export function vatNumberIssue(value: string): VatIssue | null {
  const normalised = normaliseVatNumber(value);
  if (normalised === "") return null;
  const format = VAT_FORMATS.get(normalised.slice(0, 2));
  if (!format) return { kind: "prefix" };
  if (!format.block.test(normalised.slice(2))) {
    return { kind: "format", country: format.country, example: format.example };
  }
  return null;
}

/**
 * The country's name in the active UI language.
 *
 * `Intl.DisplayNames` carries the CLDR names for every locale the browser ships,
 * so the alternative — 28 country names entered twice by hand, and again for the
 * next language — buys nothing. The ISO code is the fallback if a runtime has no
 * region data rather than a thrown error inside a validation message.
 */
export function countryName(code: string, language: string): string {
  try {
    return new Intl.DisplayNames([language], { type: "region" }).of(code) ?? code;
  } catch {
    return code;
  }
}
